import type { ApiClient } from '@carbonwebmail/api-client'
import { useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { Dialog } from '../../ui/Dialog.js'
import { Field } from '../../ui/Field.js'
import { PillButton } from '../../ui/PillButton.js'

/**
 * Giving a readable name to contacts that have none.
 *
 * **Written because a monday import left 4,032 people called "Item".** monday
 * falls back to the item's own name when nothing is mapped to the name, and on
 * a board whose items are all called that, every contact gets it. A list where
 * every row reads the same word cannot be used for anything.
 *
 * **It replaces one exact word, and blanks.** Not a pattern, not a guess about
 * which names look unreal — those would eventually rewrite somebody's actual
 * name, and there is no undo. Typing the word is what makes the scope something
 * a person chose rather than something the product decided.
 *
 * The new name comes from the address: what is before the `@`, with dots and
 * underscores as spaces, each word capitalised. `dana.scully@…` is Dana Scully.
 */
export function FixNamesDialog({
  client,
  onFixed,
  onClose,
}: {
  readonly client: ApiClient
  /** Told when names actually changed, so the board behind reloads. */
  readonly onFixed: () => void
  readonly onClose: () => void
}) {
  const [placeholder, setPlaceholder] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | undefined>(undefined)
  const [problem, setProblem] = useState<string | undefined>(undefined)

  const fix = async () => {
    setBusy(true)
    setProblem(undefined)
    setNotice(undefined)
    try {
      const word = placeholder.trim()
      const changed = await client.fixContactNames(word === '' ? undefined : word)

      setNotice(
        changed === 0
          ? 'No names matched, so nothing changed.'
          : `${changed === 1 ? '1 name was' : `${changed} names were`} set from their address.`,
      )
      if (changed > 0) onFixed()
    } catch {
      setProblem('We could not fix those names. Nothing was changed — try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog heading="Fix names" onClose={onClose}>
      <p className="mb-4 text-[13px] text-t2 leading-relaxed">
        Contacts with no name get one from their address — what comes before the{' '}
        <strong className="text-t1">@</strong>, so <em>dana.scully@northwind.test</em> becomes{' '}
        <strong className="text-t1">Dana Scully</strong>.
      </p>

      <Field
        disabled={busy}
        hint="Leave this empty to fix only contacts with no name at all. An import can leave thousands sharing one word — monday calls them “Item” — and this replaces exactly that word, never anything like it."
        id="fix-names-placeholder"
        label="Also replace names that are exactly"
        onChange={setPlaceholder}
        type="text"
        value={placeholder}
      />

      {problem === undefined ? null : (
        <div className="mt-4">
          <Alert>{problem}</Alert>
        </div>
      )}
      {notice === undefined ? null : (
        <div className="mt-4">
          <Alert tone="unknown">{notice}</Alert>
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <PillButton disabled={busy} onClick={onClose} type="button" variant="secondary">
          Close
        </PillButton>
        <PillButton disabled={busy} onClick={() => void fix()} type="button" variant="primary">
          {busy ? 'Fixing…' : 'Fix names'}
        </PillButton>
      </div>
    </Dialog>
  )
}
